import { useEffect, useState } from "react";
import { Box, Button, Typography, Stack, Grid } from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import MpgInput from "../../../../CommonFormComp/MpgInput";
import MpgItem from "../../../../CommonFormComp/MpgItem";
import MpgAutocompleteSingle from "../../../../CommonFormComp/MpgAutocompleteSingle";
import {
  useGetSubAcccountByClientAccountIdQuery,
  useGetLanguageListByClientAccountOrClientSubAccountId,
  useUpdateClientUserMutation,
  useDeleteClientUserMutation,
  useGetClientAccountDropdownMutation} from "../../../../reactQueries/AppApi/rqHooksForClientUsers";

import { useGetPermissionsListQuery, useGetDashboardAccessListQuery, useSendEmailNotiMutation, useGetServiceOfficeListMutation, useGetSiteCoreCMSData } from "../../../../reactQueries/commonRQHooks";
import CircularProgress from '@mui/material/CircularProgress';
import MpgServiceOfficeDialog from "../../../../CommonFormComp/MpgServiceOfficeListDialog/MpgServiceOfficeDialog";
import { clientUserViewPath } from "../../../../routes/routePaths";
import MpgErrorDialog from "../../../../common/dialog/MpgErrorDialog";
import MpgSuccessDialog from "../../../../common/dialog/MpgSuccessDialog";
import MpgLoadingDialog from "../../../../common/dialog/MpgLoadingDialog";
import MpgAutocompleteMulti from "../../../../CommonFormComp/MpgAutocompleteMulti";
import WarningAlert from "../../../../common/dialog/alerts/WarningAlert";
import MpgConfirmDialog from "../../../../common/dialog/MpgConfirmDialog";
import { useGetLoggedInUserDetails } from "../../../../hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import useTrackUserActivity from "../../../../userActivityTracking/useTrackUserActivity";
import { getCLPermissions } from "../../../../hooksAndFunctions/functions/getCLPermissions";
import { getAccessTypes } from "../../../../hooksAndFunctions/functions/getAccessTypes";
import { useGetAccountAndSubAccListOnlyForA4Role } from "../../../../hooksAndFunctions/customHooks/useGetAccountAndSubAccListOnlyForA4Role";
import { BaseSchema } from "../BaseSchema";


const EditClientUser = ({ clientUserDataById, ClientTypeList }) => {
  const { Id: clientUserId } = useParams()

  const { loggedInUser } = useGetLoggedInUserDetails();
  const { clientPortalAdminRoleId, isEnterpriseAdmin, id: userId } = loggedInUser;

  let navigate = useNavigate();

  const nameUser = JSON.parse(window.sessionStorage.getItem("UserAccount"))?.[
    "name"
  ];

  const {
    clientPortalAdminType
  } = getAccessTypes()
  const {
    hasA2Access,
    hasA4Access
  } = clientPortalAdminType

  /**useful for only A4 role */
  const { clientAccountIdListForA4Role, clientSubAccountIdListForA4Role } = useGetAccountAndSubAccListOnlyForA4Role({ userId })

  // schema validations
  const schema = yup.object().shape({
    ...BaseSchema,
    client_sub_account: yup.object().label('This field')
      .when('permission_level', (permission_level, yup) => {
        const { CL1_CL2_CL9_Permission } = getCLPermissions({ selectedPermissionLabel: permission_level?.selectOptionLabel })

        if (!CL1_CL2_CL9_Permission) {
          return yup.required().typeError('This field is a required')
        } else {
          return yup.nullable()
        }
      }),
    // .optional().nullable(),
    service_office: yup.string().label('This field')
      .when('permission_level', (permission_level, yup) => {
        const { CL6_CL7_CL8_Permission } = getCLPermissions({ selectedPermissionLabel: permission_level?.selectOptionLabel })

        if (!!CL6_CL7_CL8_Permission) {
          return yup.required().typeError('This field is a required')
        } else {
          return yup.nullable()
        }
      }),
    dashboard_access: yup.array().label('This field')
      .when('permission_level', (permission_level, yup) => {
        const { CL4_CL7_Permission } = getCLPermissions({ selectedPermissionLabel: permission_level?.selectOptionLabel })

        if (!!CL4_CL7_Permission) {
          return yup.optional()
        } else {
          /**way to handle required for yup array */
          return yup.min(1, "This field is a required").typeError('This field is a required')
        }
      }),
    pref_lang: yup.object().label('This field')
      .required()
      .typeError("This field is a required"),
  });

  const {data: adminClientUserCMSDataFromAPI} = useGetSiteCoreCMSData({pagename: "ClientUsers"});

  const [clientUsersCMSData, setClientUsersCMSData] = useState({
    title: "",
    description: "",
    tabNavigationTitle: "",
    pageContent: {},
  });

  // setting default values
  const defaultValues = {
    first_name: clientUserDataById?.FirstName,
    last_name: clientUserDataById?.LastName,
    email: clientUserDataById?.EmailAddress,
    job_role: clientUserDataById?.JobRole,
    status: clientUserDataById?.StatusDescription, 
    isRegistered: !!clientUserDataById?.IsRegistered ? "Yes" : clientUserDataById?.LastLogin ? "Incomplete" : "No",
  };

  const rhfFormInstance = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });
  const { watch, setValue } = rhfFormInstance;

  const selectedEditPermissionLevel = watch("permission_level");
  const selectedEditClientAccount = watch("client_account");
  const selectedEditClientSubAccount = watch("client_sub_account");

  const selectedPermissionLabel = selectedEditPermissionLevel?.selectOptionLabel || ""
  /** getting permissions type */
  const [CLPermission, setCLPermission] = useState({})

  const { CL1_CL2_CL9_Permission, CL3_CL4_CL5_Permission, CL6_CL7_CL8_Permission, CL4_CL7_Permission } = CLPermission

  useEffect(() => {
    if (!!selectedPermissionLabel) {
      const CLPermission = getCLPermissions({ selectedPermissionLabel })
      setCLPermission(CLPermission)
    } else {
      setCLPermission({})
    }
  }, [selectedPermissionLabel])


  // api - get permissions list
  const {
    data: permissionListData,
  } = useGetPermissionsListQuery();

  // api - get client accounts list
  const { mutate: getClientAccountList,
    data: clientAccountList } = useGetClientAccountDropdownMutation();

  useEffect(() => {
    getClientAccountList({ loggedInUserId: userId, roleId: clientPortalAdminRoleId, isEnterpriseAdmin })
  }, [loggedInUser])

  const clientAccountListData = clientAccountList?.data

  // api - get dashboard access list
  const { data: dashboardAccessListData,
  } = useGetDashboardAccessListQuery();

  const { data: clientSubAccListData,
  } = useGetSubAcccountByClientAccountIdQuery({ ClientAccountId: selectedEditClientAccount?.id });

  const {
    mutate: getServiceOfficeListData,
    isLoading: isLoadingOnServiceOfficeList,
    isSuccess: isSuccessOnServiceOfficeList,
    data: serviceOfficeListData,
  } = useGetServiceOfficeListMutation();

  useEffect(() => {
    getServiceOfficeListData({ body: [] })
  }, [])

  // /**disable dependent fields */
  const [disableClientAccount, setDisableClientAccount] = useState(true)
  const [disableClientSubAccount, setDisableClientSubAccount] = useState(true)
  const [disableServiceOffice, setDisableServiceOffice] = useState(true)
  const [disableLangList, setDisableLangList] = useState(true)

  const [clientAccountOrClientSubAccountId, setClientAccountOrClientSubAccountId] = useState('')

  useEffect(() => {

    if (!!selectedEditPermissionLevel?.selectOptionLabel && !!CLPermission) {
      /**lang */
      setDisableLangList(false)

      if (!!CL1_CL2_CL9_Permission) {        
        setClientAccountOrClientSubAccountId(selectedEditClientAccount?.id)

        setDisableClientAccount(false)
        setDisableClientSubAccount(true)
        setDisableServiceOffice(true)

        setValue('client_sub_account', "")
        setValue('service_office', '')

      } else if (!!CL3_CL4_CL5_Permission) {
        setClientAccountOrClientSubAccountId(selectedEditClientSubAccount?.id)

        setDisableClientAccount(false)
        setDisableClientSubAccount(false)
        setDisableServiceOffice(true)

        setValue('service_office', "")
      } else {
        setClientAccountOrClientSubAccountId(selectedEditClientSubAccount?.id)

        setDisableClientAccount(false)
        setDisableClientSubAccount(false)
        setDisableServiceOffice(false)
      }
    }
  }, [selectedEditPermissionLevel, selectedEditClientSubAccount, selectedEditClientAccount, CLPermission])

  const { data: langListByClientAccountOrClientSubAccountIdData,
    isSuccess: isSuccessOnlangListByClientAccountOrClientSubAccountIdData,
  } = useGetLanguageListByClientAccountOrClientSubAccountId({ ClientAccountOrClientSubAccountId: clientAccountOrClientSubAccountId });

  // api - update client user mutation
  const {
    mutate: updateClientUser,
    isError: isErrorOnUpdate,
    isLoading: isLoadingOnUpdate,
    isSuccess: isSuccessOnUpdate,
    error: errorOnUpdate,
  } = useUpdateClientUserMutation();

  // api - delete client user mutation
  const {
    mutate: deleteClientUserById,
    isError: isErrorOnDelete,
    isLoading: isLoadingOnDelete,
    isSuccess: isSuccessOnDelete,
    variables: variablesOnDelete
  } = useDeleteClientUserMutation();

  const {
    mutate: sendEmailNotification,
    isError: isErrorOnEmailNoti,
    isLoading: isLoadingOnEmailNoti,
    isSuccess: isSuccessOnEmailNoti,
  } = useSendEmailNotiMutation();

  const permissionAutocompleteList = !!permissionListData && permissionListData.map((item) => {
    return {
      id: item.PermissionId,
      selectOptionLabel: `${item.PermissionCode} - ${item.PermissionDescription}`,
      selectOptionValue: item.PermissionId,
      extraData: item
    }
  })

  const clientAccountAutocompleteList = !!clientAccountListData && clientAccountListData.map((item) => {
    const disableOptionForA4Role = !clientAccountIdListForA4Role.includes(item?.Id)

    let disableOption = false

    if (!item.IsActive) {
      disableOption = true
    } else {
      if ((!!hasA4Access || !!hasA2Access) && !!disableOptionForA4Role) {
        if (!!CL1_CL2_CL9_Permission) {
          disableOption = true
        } else {
          disableOption = false
        }
      } else {
        /**for rest of roles */
        disableOption = false
      }
    }

    return {
      id: item.Id,
      selectOptionLabel: `${item.ClientAccountName}`,
      selectOptionValue: item.Id,
      selectOptionDisabled: !!disableOption,
      extraData: item
    }
  })
  /**need change from API team */
  const clientSubAcctAutocompleteList = !!clientSubAccListData && clientSubAccListData.map((item) => {
  //const clientSubAcctAutocompleteList = !!clientSubAccListData?.data && clientSubAccListData?.data.map((item) => {
    const disableOptionForA4Role = !clientSubAccountIdListForA4Role.includes(item?.Id)

    let disableOption = false

    /**disable option is client sub acc is Inactive */
    if (!item.IsActive) {
      disableOption = true
    } else {
      /**(only for A4 role) get all clinet sub acc ids where this user is not A4 admin and diable those autocomplete options */
      if ((!!hasA4Access || !!hasA2Access) && !!disableOptionForA4Role) {
        disableOption = true
      } else {
        /**for rest of roles */
        disableOption = false
      }
    }

    return {
      id: item.Id,
      selectOptionLabel: `${item.ClientAccountName}`,/**ClientAccountName=> ClientSubAccountName*/
      selectOptionValue: item.Id,
      selectOptionDisabled: !!disableOption,
      extraData: item
    }
  })

  const dashBoardAccessAutocompleteList = !!dashboardAccessListData && dashboardAccessListData.map((item) => {
    return {
      id: item.id,
      selectOptionLabel: `${item.TypeDescription}`,
      selectOptionValue: item.id,
      extraData: item
    }
  })

  const ClientTypeAutocompleteList = !!ClientTypeList && ClientTypeList.map((item) => {
        return {
            id: item.DataClassificationKey,
            selectOptionLabel: `${item.MasterDataClassificationName}`,
            selectOptionValue: item.DataClassificationKey,
        }
    })

  /**Setting existing selected values from DB to repective dropdowns */
  const [existingDashboardAccessIds, setExistingDashboardAccessIds] = useState([])

  useEffect(() => {
    if (!!dashboardAccessListData && !!dashboardAccessListData.length) {
      const filteredItems = dashboardAccessListData.filter((item) => {
        const dashboardAccessIdArray = clientUserDataById?.ClientUserDashboardId

        if (!!dashboardAccessIdArray) {
          if (dashboardAccessIdArray.includes(item.id)) {
            return `${item.id}`
          }
        }
      });

      if (!!filteredItems && !!filteredItems.length) {

        const vals = filteredItems.map((item) => {
          return {
            id: item.id,
            selectOptionLabel: `${item.TypeDescription}`,
            selectOptionValue: item.id,
            extraData: item
          };
        })

        setValue("dashboard_access", vals || [], {
          shouldValidate: true,
        });

        setExistingDashboardAccessIds(vals)
      }
    }
  }, [dashboardAccessListData, clientUserDataById]);
  /**dashboard field control on CL4 and CL7 */
  useEffect(() => {
    if (!!selectedEditPermissionLevel?.selectOptionLabel) {
      if (!!CL4_CL7_Permission) {
        /** clearing dashboard values on CL4 and CL7 */
        setValue("dashboard_access", [])
      } else {
        /**by default all values needs to be selected */
        setValue("dashboard_access", existingDashboardAccessIds, { validate: true })
      }
    }
  }, [selectedEditPermissionLevel, CLPermission])

  // updating permission level default value
  useEffect(() => {
    if (!!permissionListData && !!permissionListData.length) {
      const foundItem = permissionListData.find((item) => {
        return item.PermissionId === clientUserDataById.PermissionId
      });

      if (!!foundItem) {
        const selectedValue = {
          id: foundItem.PermissionId,
          selectOptionLabel: `${foundItem.PermissionCode} - ${foundItem.PermissionDescription}`,
          selectOptionValue: foundItem.PermissionId,
          extraData: foundItem
        };
        setValue("permission_level", selectedValue || "", {
          shouldValidate: true,
        });
      }
    }
  }, [permissionListData, clientUserDataById]);

  useEffect(() => {
    if (!!clientAccountListData && !!clientAccountListData.length) {
      const foundItem = clientAccountListData.find((item) => {
        return item.Id === clientUserDataById.ClientAccountId
      });

      if (!!foundItem) {
        const selectedValue = {
          id: foundItem.Id,
          selectOptionLabel: `${foundItem.ClientAccountName}`,
          selectOptionValue: foundItem.Id,
          extraData: foundItem
        };
        setValue("client_account", selectedValue || "", {
          shouldValidate: true,
        });
      }
    }
  }, [clientAccountListData, clientUserDataById]);

  useEffect(() => {
    //if (!!clientSubAccListData?.data && !!clientSubAccListData?.data?.length) {
      //const foundItem = clientSubAccListData?.data?.find((item) => {
    if (!!clientSubAccListData && !!clientSubAccListData.length) {
    const foundItem = clientSubAccListData.find((item) => {
        return item.Id === clientUserDataById.ClientSubAccountId
      });

      if (!!foundItem) {
        const selectedValue = {
          id: foundItem.Id,
          selectOptionLabel: `${foundItem.ClientAccountName}`,/**ClientAccountName=> ClientSubAccountName*/
          selectOptionValue: foundItem.Id,
          extraData: foundItem
        };
        setValue("client_sub_account", selectedValue || "", {
          shouldValidate: true,
        });
      }
    }
  }, [clientSubAccListData, clientUserDataById]);


  /**preferred language list */
  const [prefLangList, setPrefLangList] = useState()
  useEffect(() => {
    if (!!isSuccessOnlangListByClientAccountOrClientSubAccountIdData && !!langListByClientAccountOrClientSubAccountIdData) {

      const list = !!langListByClientAccountOrClientSubAccountIdData && langListByClientAccountOrClientSubAccountIdData.map((item) => {
        return {
          id: item.LanguageID,
          selectOptionLabel: `${item.LanguageCode} - ${item.LanguageDescription}`,
          selectOptionValue: item.LanguageID,
          extraData: item
        }
      })
      setPrefLangList(list)
    }
  }, [isSuccessOnlangListByClientAccountOrClientSubAccountIdData])
  /**setting existing lang list */
  useEffect(() => {
    if (!!langListByClientAccountOrClientSubAccountIdData && !!langListByClientAccountOrClientSubAccountIdData.length) {
      const foundItem = langListByClientAccountOrClientSubAccountIdData.find((item) => {
        return item.LanguageID === clientUserDataById.LanguageId
      });

      if (!!foundItem) {
        const selectedValue = {
          id: foundItem.LanguageID,
          selectOptionLabel: `${foundItem.LanguageCode} - ${foundItem.LanguageDescription}`,
          selectOptionValue: foundItem.LanguageID,
          extraData: foundItem
        };
        setValue("pref_lang", selectedValue);
      }
    }
  }, [langListByClientAccountOrClientSubAccountIdData]);

  /**service office handling */
  /**service office dialog state */
  const [openServiceOfficeDialog, setOpenServiceOfficeDialog] = useState(false);

  /**service office selected data state */
  const [serviceOfficeItemsFromDialog, setServiceOfficeItemsFromDialog] = useState([])

  /**handling existing entry from db on edit for service offices */
  useEffect(() => {
    if (!!serviceOfficeItemsFromDialog && serviceOfficeItemsFromDialog.length) {
      const list = serviceOfficeItemsFromDialog.map((item) => {
        return item.masterRightOfficeName
      })
      setValue('service_office', list.toString())
    } else {
      setValue("service_office", "");
    }
  }, [serviceOfficeItemsFromDialog])

  useEffect(() => {
    if (!!clientUserDataById && !!clientUserDataById?.ClientAccountUserServiceOffice) {

      let existingServiceOfficeObjList = []
      let existingServiceOfficeOnlyNameList = []

      clientUserDataById?.ClientAccountUserServiceOffice.forEach((eachServiceOffice) => {
        const foundItem = !!serviceOfficeListData?.data && serviceOfficeListData?.data.find((item) => {
          return item.rightOfficeKey === eachServiceOffice.rightOfficeKey;
        });

        if (!!foundItem) {
          existingServiceOfficeObjList.push(foundItem)
          existingServiceOfficeOnlyNameList.push(foundItem.masterRightOfficeName)
        }
      })
      setValue("service_office", existingServiceOfficeOnlyNameList.toString());
      /**setting values to dialog too */
      setServiceOfficeItemsFromDialog(existingServiceOfficeObjList)

    }
  }, [serviceOfficeListData?.data, clientUserDataById]);

  useEffect(() => {
          
          if (clientUserDataById?.DataClassificationKey && ClientTypeList) {
          const existingClientTypeDefaultList = ClientTypeList
            .filter(item => item.DataClassificationKey == clientUserDataById.DataClassificationKey)
            .map(item => ({
              id: item.DataClassificationKey,
              selectOptionLabel: `${item.MasterDataClassificationName}`,
              selectOptionValue: item.DataClassificationKey,
            }));
          setValue('ClientType', existingClientTypeDefaultList[0] || "", { shouldValidate: true });
        } else {
          setValue('ClientType', "");
        }
      }, [clientUserDataById?.DataClassificationKey, ClientTypeList])

  /**handling submit func */
  const onSubmiting = (data) => {

    let serviceOfficeLst = []
    if (!!serviceOfficeItemsFromDialog) {
      serviceOfficeItemsFromDialog.forEach((item) => {
        serviceOfficeLst.push({
          rightOfficeKey: item?.rightOfficeKey,
        })
      })
    }

    let clientSubAccIdTobeSent = ""
    let serviceOfficeListTobeSent = []
    if (!!CL1_CL2_CL9_Permission) {
      clientSubAccIdTobeSent = "";
      serviceOfficeListTobeSent = [];
    }
    else if (!!CL3_CL4_CL5_Permission) {
      clientSubAccIdTobeSent = data?.client_sub_account?.id;
      serviceOfficeListTobeSent = [];
    }
    else if (!!CL6_CL7_CL8_Permission) {
      clientSubAccIdTobeSent = data?.client_sub_account?.id;
      serviceOfficeListTobeSent = serviceOfficeLst;
    }

    let dashboardAccessIds = [];
    if (!!data?.dashboard_access && !!data?.dashboard_access?.length) {
      const list = []
      data?.dashboard_access.forEach((item) => {
        list.push(item?.id)
      })
      dashboardAccessIds = list
    }

    const body = {
      UserId: userId,
      Id: clientUserId,
      FirstName: data?.first_name || '',
      LastName: data?.last_name || '',
      ContactNumber: data?.contact_no || '',
      EmailAddress: data?.email || '',
      JobRole: data?.job_role || '',

      PermissionId: data?.permission_level.id,
      DataClassificationKey:data?.ClientType?.id,
      ClientAccountId: data?.client_account?.id,
      ClientSubAccountId: clientSubAccIdTobeSent,
      ClientAccountUserServiceOffice: serviceOfficeListTobeSent,
      ClientUserDashboardId: dashboardAccessIds || [],
      LanguageId: data?.pref_lang?.id,
      UpdatedBy: nameUser,
      StatusCode: clientUserDataById?.StatusCode,/**need to send only for update and not for create */
    };
    updateClientUser({ body });
  };

  useEffect(() => {
    if (!!isSuccessOnUpdate || !!isSuccessOnDelete) {
      navigate(clientUserViewPath);
    }
  }, [isSuccessOnUpdate, isSuccessOnDelete]);

  const [isClientUserPartOfDeactivatedAccOrSubAcc, setIsClientUserPartOfDeactivatedAccOrSubAcc] = useState(false)

  useEffect(() => {
    if (!!clientUserDataById?.ClientAccountId) {
      if (!clientUserDataById?.ClientAccountIsActive) {
        setIsClientUserPartOfDeactivatedAccOrSubAcc(true)
      } else {
        if (!!clientUserDataById?.ClientSubAccountId) {
          if (!clientUserDataById?.ClientSubAccountIsActive) {
            setIsClientUserPartOfDeactivatedAccOrSubAcc(true)
          } else {
            setIsClientUserPartOfDeactivatedAccOrSubAcc(false)
          }
        }
      }
    }

  }, [clientUserDataById])

  /**clearing service office selections in dialog if permissions does not belongs to cl6 cl7 cl8 */
  useEffect(() => {
    if (!CL6_CL7_CL8_Permission) {
      setServiceOfficeItemsFromDialog([])
    }
  }, [selectedPermissionLabel, CLPermission])

  /**confirm dialog state on remove action */
  const [openMpgConfirmDialog, setOpenMpgConfirmDialog] = useState(false)

  /**tracking user Activity */
  const { trackActivity } = useTrackUserActivity()
  useEffect(() => {
    if (!!isSuccessOnDelete) {
      let actionType = ""
      if (!!variablesOnDelete?.isRemovedMode) {
        actionType = "Removed"
      } else if (!!variablesOnDelete?.isDeleteMode) {
        actionType = "Deactivated"
      } else if (!variablesOnDelete?.isDeleteMode) {
        actionType = "Activated"
      }

      trackActivity({
        activitySuccess: !!isSuccessOnDelete,
        actionType: actionType,
        itemId: variablesOnDelete?.Id || "",
      })
    }

    if (!!isSuccessOnEmailNoti) {
      trackActivity({
        activitySuccess: !!isSuccessOnEmailNoti,
        actionType: "EmailInvitationSent",
        itemId: clientUserDataById?.Id || "",
      })
    }
  }, [variablesOnDelete, isSuccessOnDelete, isSuccessOnEmailNoti])

  /**handling error states and msgs */
  const [errorTitle, setErrorTitle] = useState("")
  const [errorContent, setErrorContent] = useState("")
  useEffect(() => {
    if (!!isErrorOnUpdate && errorOnUpdate?.response?.data === "EmailExists") {
      setErrorTitle("Email exists. Try again...!")
      setErrorContent(`"${watch('email')}" exists already in our records. Please, try again with different email id.`)
    } else if (!!isErrorOnUpdate && errorOnUpdate?.response?.data === "NoAccessForA4Role") {
      setErrorTitle("Unauthorized action...!")
      setErrorContent("You are not authorized to perform this action.")
    } else {
      setErrorTitle('')
      setErrorContent("")
    }
  }, [isErrorOnUpdate, errorOnUpdate])

  const fetchClientUsersCMSData = () =>{
    if(adminClientUserCMSDataFromAPI && adminClientUserCMSDataFromAPI.Pages){
      adminClientUserCMSDataFromAPI.Pages.map((el) => {
       if (el.Key === "ClientUsers") {
         const { Title, Description, TabNavigationTitle, Components } = el;
         setClientUsersCMSData({ title: Title, description: Description, tabNavigationTitle: TabNavigationTitle, pageContent: Components });
       }
   });
    }
  }

  const fetchClientUserListLabel = (key) => {
    if (clientUsersCMSData && clientUsersCMSData?.pageContent["ClientUsersList"]) {
        const meetingReqCMSDataLabel = clientUsersCMSData.pageContent["ClientUsersList"];
        return returnLabel(meetingReqCMSDataLabel?.Labels, key)
    }
    return '';
  }

  const fetchClientUserDtlsLabel = (key) => {
    if (clientUsersCMSData && clientUsersCMSData?.pageContent["ClientUsersDetails"]) {
        const meetingReqCMSDataLabel = clientUsersCMSData.pageContent["ClientUsersDetails"];
        return returnLabel(meetingReqCMSDataLabel?.Labels, key)
    }
    return '';
  }

  const fetchClientUserAccDtlsLabel = (key) => {
    if (clientUsersCMSData && clientUsersCMSData?.pageContent["ClientUsersAccessDetails"]) {
        const meetingReqCMSDataLabel = clientUsersCMSData.pageContent["ClientUsersAccessDetails"];
        return returnLabel(meetingReqCMSDataLabel?.Labels, key)
    }
    return '';
  }

  const fetchCommonCMSLabel = (key) => {
    if (clientUsersCMSData && clientUsersCMSData?.pageContent["CommonComponent"]) {
        const meetingReqCMSDataLabel = clientUsersCMSData.pageContent["CommonComponent"];
        return returnLabel(meetingReqCMSDataLabel?.Labels, key)
    }
    return '';
  }

  const returnLabel = (labelArray=[], key) => {
        return labelArray.find((x) => x.Key === key)?.Phrase || '';
  }

  useEffect(() => {
    fetchClientUsersCMSData();
   }, [adminClientUserCMSDataFromAPI]);

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Typography variant="h5" align="center" sx={{ fontWeight: "bolder" }}>
          {/* Edit Client User */}
          {fetchClientUserDtlsLabel('ClientUsersDetails.EditClientUser')}
        </Typography>

        {
          !!isClientUserPartOfDeactivatedAccOrSubAcc && (<WarningAlert
            // warningMsg={
            //   `Either client account or client sub-account or both, from where the client user is belonged, are in deactivated status.`
            // }
            warningMsg={fetchClientUserDtlsLabel('ClientUsersDetails.AccountDeactivatedMsg')}
          />)
        }

        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          m={1}
        >
          <Button
            variant="outlined"
            size="small"
            component={Link}
            to={clientUserViewPath}
          >
            {/* Client Users */}
            {fetchClientUserDtlsLabel('ClientUsersDetails.ClientUsers') }
          </Button>

          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const statusDescription = clientUserDataById?.StatusDescription

              if (statusDescription === "Active") {
                deleteClientUserById({ isDeleteMode: true, isRemovedMode: false, Id: clientUserId });
              } else {
                deleteClientUserById({ isDeleteMode: false, isRemovedMode: false, Id: clientUserId });
              }
            }}
            /**if account or sub account is deactivated then disable this button */
            disabled={!!isClientUserPartOfDeactivatedAccOrSubAcc}
          >
            {/* {clientUserDataById?.StatusDescription === "Active"
              ? "Deactivate User"
              : "Reactivate User"} */}
            {
              clientUserDataById?.StatusDescription === "Active"
              ? fetchClientUserDtlsLabel('ClientUsersDetails.DeactivateUser')
              : fetchClientUserDtlsLabel('ClientUsersDetails.ReactivateUser')
            }
          </Button>
        </Stack>

        <Box
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={rhfFormInstance.handleSubmit(onSubmiting)}
        >
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <MpgItem sx={{ padding: { xs: "20px" } }}>
                <Typography
                  variant="h6"
                  align="center"
                  sx={{ fontWeight: "bolder" }}
                >
                  {/* Client User Details */}
                  {fetchClientUserDtlsLabel('ClientUsersDetails.ClientUserDetails')}
                </Typography>
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"first_name"}
                  label={fetchClientUserDtlsLabel('ClientUsersDetails.FirstName')} //{"First Name"}
                  fullWidth
                />
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"last_name"}
                  label={fetchClientUserDtlsLabel('ClientUsersDetails.LastName')} //{"Last Name"}
                  fullWidth
                />
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"email"}
                  label={fetchClientUserDtlsLabel('ClientUsersDetails.Email')} //{"Email"}
                  fullWidth
                  disabled
                />
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"job_role"}
                  label={fetchClientUserDtlsLabel('ClientUsersDetails.JobRole')} //{"Job Role"}
                  fullWidth
                />
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"status"}
                  label={fetchClientUserDtlsLabel('ClientUsersDetails.Status')} //{"Status"}
                  fullWidth
                  disabled={true}
                />
                 <MpgAutocompleteSingle 
                  rhfFormInstance={rhfFormInstance}
                  name={"ClientType"}
                  label={fetchClientUserDtlsLabel('ClientUsersDetails.Type')} 
                  fullWidth
                  optionList={ClientTypeAutocompleteList || []}
                />
                <MpgInput
                  rhfFormInstance={rhfFormInstance}
                  name={"isRegistered"}
                  label={fetchClientUserListLabel('ClientUsersList.Registration')} //{"Registration"}
                  disabled
                  fullWidth
                />

                <Button
                  variant="outlined"
                  size="medium"
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={() => {
                    setOpenMpgConfirmDialog(true)
                  }}
                  disabled={clientUserDataById?.StatusDescription === "Removed"}
                >
                  {/* Remove User */}
                  {fetchClientUserDtlsLabel('ClientUsersDetails.RemoveUser')} 
                </Button>

                <Button
                  variant="outlined"
                  size="medium"
                  fullWidth
                  sx={{ mt: 2 }}
                  onClick={() => {
                    sendEmailNotification({ body: [{ Email: clientUserDataById?.EmailAddress }] });
                  }}
                  /**if account or sub account is deactivated then disable this button */
                  disabled={!!isClientUserPartOfDeactivatedAccOrSubAcc}
                >
                  {/* Resend Invitation Email */}
                  {fetchClientUserDtlsLabel('ClientUsersDetails.ResendInvitationEmail')} 
                </Button>
              </MpgItem>
            </Grid>
            <Grid item xs={12} sm={6}>
              <MpgItem>
                <Typography
                  variant="h6"
                  align="center"
                  sx={{ fontWeight: "bolder" }}
                >
                  {/* Access Details */}
                  {clientUsersCMSData.pageContent["ClientUsersAccessDetails"]?.Title}
                </Typography>
                <MpgAutocompleteSingle
                  rhfFormInstance={rhfFormInstance}
                  name={"permission_level"}
                  label={fetchClientUserAccDtlsLabel('ClientUsersAccessDetails.PermissionLevel')} //{"Permission Level"}
                  fullWidth
                  optionList={permissionAutocompleteList || []}
                />
                <MpgAutocompleteSingle
                  rhfFormInstance={rhfFormInstance}
                  name={"client_account"}
                  label={fetchClientUserAccDtlsLabel('ClientUsersAccessDetails.ClientAccount')} //{"Client Account"}
                  fullWidth
                  optionList={clientAccountAutocompleteList || []}
                  onChangeExtraHandler={() => {
                    setValue("client_sub_account", "");
                    setValue("service_office", "");
                    setValue("pref_lang", "");
                  }}
                />
                <MpgAutocompleteSingle
                  rhfFormInstance={rhfFormInstance}
                  name={"client_sub_account"}
                  label={fetchClientUserAccDtlsLabel('ClientUsersAccessDetails.ClientSubAccount')} //{"Client Sub-Account"}
                  fullWidth
                  optionList={clientSubAcctAutocompleteList || []}
                  onChangeExtraHandler={() => {
                    setValue("service_office", "");
                    setValue("pref_lang", "");
                  }}
                  disabled={!!disableClientSubAccount}
                />

                <div>
                  <MpgInput
                    rhfFormInstance={rhfFormInstance}
                    label={fetchClientUserAccDtlsLabel('ClientUsersAccessDetails.ServiceOffices')} //{'Service Offices'}
                    name={'service_office'}
                    multiline
                    rows={4}
                    fullWidth
                    disabled={true}
                  />
                  {

                    !!isLoadingOnServiceOfficeList && (
                      <Button
                        disabled
                      >
                        <CircularProgress
                          size={15}
                          sx={{ margin: '5px' }}
                        />
                        {/* Fetching... */}
                        {fetchCommonCMSLabel('Common.Fetching')}
                      </Button>
                    )
                  }
                  {
                    !!isSuccessOnServiceOfficeList && (
                      <Button
                        onClick={() => { setOpenServiceOfficeDialog(true) }}
                        disabled={!!disableServiceOffice}
                      >
                        {/* Select Service Offices */}
                        {fetchClientUserAccDtlsLabel('ClientUsersAccessDetails.SelectServiceOffices')}
                      </Button>
                    )}
                </div>

                <MpgAutocompleteMulti
                  rhfFormInstance={rhfFormInstance}
                  name={"dashboard_access"}
                  label={fetchClientUserAccDtlsLabel('ClientUsersAccessDetails.DashboardAccess')} //{"Dashboard Access"}
                  fullWidth
                  optionList={dashBoardAccessAutocompleteList || []}
                  disabled={!!CL4_CL7_Permission}
                />

                <MpgAutocompleteSingle
                  rhfFormInstance={rhfFormInstance}
                  name={"pref_lang"}
                  label={fetchClientUserAccDtlsLabel('ClientUsersAccessDetails.PreferredLanguage')} //{"Preferred Language"}
                  fullWidth
                  optionList={prefLangList || []}
                  disabled={!!disableLangList}
                />
              </MpgItem>
            </Grid>
          </Grid>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent={"space-evenly"}
            mt={3}
          >
            <Button
              variant="outlined"
              type="submit"
              size="small"
              /**if account or sub account is deactivated then disable this button */
              disabled={!!isClientUserPartOfDeactivatedAccOrSubAcc}
            >
              {/* Update User */}
              {fetchClientUserDtlsLabel('ClientUsersDetails.UpdateUser')}
            </Button>
          </Stack>
        </Box>

        {  /**dialog */}
        <MpgServiceOfficeDialog
          open={openServiceOfficeDialog}
          setOpen={setOpenServiceOfficeDialog}

          serviceOfficeItemsFromDialog={serviceOfficeItemsFromDialog || []}
          setServiceOfficeItemsFromDialog={setServiceOfficeItemsFromDialog}

          serviceOfficeListFromAPI={serviceOfficeListData?.data || []}
        />

        {/** error dialog on update action */}
        <MpgErrorDialog
          open={!!isErrorOnUpdate || !!isErrorOnDelete || !!isErrorOnEmailNoti}
          title={errorTitle || ''}
          content={errorContent || ''}
        />
        {/** for email notification */}
        <MpgSuccessDialog
          content={fetchClientUserDtlsLabel('ClientUsersDetails.EmailSuccessMsg')} //{`The email notification sent successfully`}
          open={!!isSuccessOnEmailNoti}
        />
        <MpgLoadingDialog
          open={
            !!isLoadingOnUpdate ||
            !!isLoadingOnDelete ||
            !!isLoadingOnEmailNoti
          }
        />

        {/** confirm dialog on remove user action */}
        <MpgConfirmDialog
          title={fetchClientUserDtlsLabel('ClientUsersDetails.Confirmyouraction')} //{`Confirm your action.`}
          content={fetchClientUserDtlsLabel('ClientUsersDetails.RemoveUserConfirmation')} //{`Are you sure you want to remove this client user.`}
          open={openMpgConfirmDialog}
          setOpen={setOpenMpgConfirmDialog}
          extraLogicHandlerOnAgree={() => {
            const statusDescription = clientUserDataById?.StatusDescription

            if (statusDescription !== "Removed") {
              deleteClientUserById({ isDeleteMode: false, isRemovedMode: true, Id: clientUserId });
            }
          }}
        />
      </Box>
    </>
  );
};

export default EditClientUser;
